import HashtagLabs from '@/components/HashtagLabs';
import HashtagLabsAd from '@/components/HashtagLabsAd';
import NewsletterPlaceholder from '@/components/NewsletterPlaceholder';
import NewsletterScript from '@/components/NewsletterScript';
import { Box, useTheme } from '@mui/material';
import PinItScript from '@orgnc/core/components/common/PinIt/PinItScript';
import { CenterLayout } from '@orgnc/core/components/primitives';
import { NewsletterSignupBlock } from '@orgnc/core/components/themes/Elements/components/blocks';
import ArticleTemplateContent from '@orgnc/core/components/themes/Elements/components/templates/ArticleTemplateContent';
import { forwardArticleRef } from '@orgnc/core/lib/themes/templates/article';
import { IArticle } from '@orgnc/core/lib/wordpress/api/adapters/articleAdapter';
import DailymotionInjector from '@/components/DailymotionInsertPlayer';
import { injectAds } from './commons';

const ArticleTemplate = forwardArticleRef((props, ref) => {
  const theme = useTheme();
  let extendedProps = {
    ...props,
    post: injectAds(props.post),
    sidebarContent: <HashtagLabsAd unit={'HalfPage_RightRail'} />,
    beforeContentSlot: <HashtagLabsAd unit={'WideBanner_Scroll'} />,
    afterContentSlot: (
      <Box
        sx={{
          '& .newsletter-inject': {
            width: 'auto',
            '@media (max-width: 600px)': {
              '& #newsletter-form-input-container': {
                width: '100%',
              },
              '& .newsletter-input-container': {
                width: '100%',
                flexDirection: 'column',
                gap: '8px',
              },
              '& #newsletter-form': {
                minWidth: 'auto',
                width: '100%',
                '& input': {
                  width: '100%',
                  marginRight: '0',
                },
                '& button': {
                  width: '100%',
                  padding: '5px',
                },
              },
            },
          },
        }}
      >
        <NewsletterPlaceholder />
        <NewsletterScript />
      </Box>
    ),
  };

  // Recursive function to modify blocks and their innerBlocks
  const processBlockRecursively = (
    block: IArticle['blocks'][0]
  ): IArticle['blocks'][0] => {
    // Process current block's originalContent if it exists
    const processedBlock = { ...block };

    // Process innerBlocks recursively if they exist
    if (
      processedBlock.innerBlocks &&
      Array.isArray(processedBlock.innerBlocks) &&
      processedBlock.innerBlocks.length > 0
    ) {
      processedBlock.innerBlocks = processedBlock.innerBlocks.map(
        (innerBlock) => processBlockRecursively(innerBlock)
      );
    }

    return processedBlock;
  };

  const modifiedBlocks: IArticle['blocks'] = extendedProps.post.blocks.map(
    (block) => processBlockRecursively(block)
  );

  extendedProps = {
    ...extendedProps,
    post: {
      ...extendedProps.post,
      blocks: modifiedBlocks,
    },
  };

  return (
    <Box ref={ref} component="article">
      <HashtagLabs />
      <CenterLayout
        maxWidth={`${theme.breakpoints.values.xl}${theme.breakpoints.unit}`}
      >
        <HashtagLabsAd
          unit={'WideBanner_Top'}
          sx={{ mt: { xs: 8, lg: 2 }, height: { xs: 100, lg: 250 } }}
        />
        <Box>
          <ArticleTemplateContent
            {...{
              ...extendedProps,
            }}
          >
            <NewsletterSignupBlock />
            <PinItScript />
          </ArticleTemplateContent>
        </Box>
        <HashtagLabsAd unit={'WideBanner_Scroll'} />
        <HashtagLabsAd unit={'Web_Interstitial'} size="out-of-page" />
        <DailymotionInjector />
      </CenterLayout>
    </Box>
  );
});

export default ArticleTemplate;
